import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import { crmPostventaV2Api } from "./crmPostventaV2Api";
import { CRM_POSTVENTA_V2_SECTIONS, getSectionMeta } from "./processCatalog";
import ProcessQueueTable from "./components/ProcessQueueTable";
import ProcessActionModal from "./components/ProcessActionModal";
import "./CRMPostventaV2Procesos.css";

const makeToday = () => new Date().toISOString().slice(0, 10);

const PROCESS_SLUGS = CRM_POSTVENTA_V2_SECTIONS.filter((item) => !item.isSpecial).map((item) => item.slug);

const escapeHtml = (value) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");

const summaryCount = (slug, processSpecificValue, fallbackValue) => {
  if (slug === "terminal-sin-facturacion") {
    return processSpecificValue ?? fallbackValue ?? 0;
  }

  return fallbackValue ?? processSpecificValue ?? 0;
};

const buildGenerationSummary = (response, slug) =>
  [
    response?.message || "Sin mensaje.",
    response?.fechaReferencia ? `Fecha de referencia del proceso: ${response.fechaReferencia}` : null,
    response?.fechaEfectivaAuditoria && response?.fechaEfectivaAuditoria !== "-" ? `Fecha efectiva de auditoria: ${response.fechaEfectivaAuditoria}` : null,
    `Registros maestros evaluados: ${response?.totalEvaluados ?? response?.candidatos ?? 0}`,
    `Elegibles: ${summaryCount(slug, response?.cantidadElegiblesTotal, response?.elegibles)}`,
    `Se crearan en esta corrida: ${summaryCount(slug, response?.cantidadAProcesar, response?.crear)}`,
    `Creados: ${response?.creados ?? 0}`,
    `Limite por ejecucion: ${response?.maximumItemsPerRun ?? 0}`,
    `Pendientes por limite: ${summaryCount(slug, response?.cantidadPendiente, response?.pendientesPorLimite)}`,
    `Excluidos por fecha de corte: ${response?.excluidosPorFechaCorte ?? 0}`,
    `Excluidos por baja: ${response?.excluidosPorBaja ?? 0}`,
    `Excluidos por datos invalidos: ${response?.excluidosPorDatosInvalidos ?? 0}`,
    `Excluidos por duplicado fuente: ${response?.excluidosPorDuplicadoFuente ?? 0}`,
    `Excluidos por proceso existente: ${response?.excluidosPorProcesoExistente ?? 0}`,
    `Excluidos por fixture: ${response?.excluidosPorFixture ?? 0}`,
    `Bloqueados: ${response?.bloqueados ?? 0}`,
    `Errores: ${response?.errores ?? 0}`,
    response?.codigoFuenteOperativa ? `Codigo fuente operativa: ${response.codigoFuenteOperativa}` : null,
    response?.ultimaFechaOperativa && response?.ultimaFechaOperativa !== "-" ? `Ultima fecha operativa valida: ${response.ultimaFechaOperativa}` : null,
    response?.ultimaFechaPago && response?.ultimaFechaPago !== "-" ? `Ultima fecha de pago: ${response.ultimaFechaPago}` : null,
    Number.isFinite(response?.diasAntiguedadFuente) ? `Dias de antiguedad de la fuente: ${response.diasAntiguedadFuente}` : null,
    Number.isFinite(response?.maximoDiasPermitidos) ? `Maximo permitido: ${response.maximoDiasPermitidos}` : null,
    Number.isFinite(response?.operacionesConFechaOperacionValida) ? `Operaciones con fecha operativa valida: ${response.operacionesConFechaOperacionValida}` : null,
    Number.isFinite(response?.operacionesConFechaOperacionFutura) ? `Operaciones con fecha operativa futura: ${response.operacionesConFechaOperacionFutura}` : null,
    Number.isFinite(response?.operacionesConFechaOperacionNula) ? `Operaciones con fecha operativa nula: ${response.operacionesConFechaOperacionNula}` : null,
    response?.maximaFechaOperacionDescartada && response?.maximaFechaOperacionDescartada !== "-" ? `Maxima fecha operativa descartada: ${response.maximaFechaOperacionDescartada}` : null,
    Number.isFinite(response?.operacionesConPagoFuturo) ? `Operaciones con pago futuro: ${response.operacionesConPagoFuturo}` : null,
    response?.operacionesConFechaOperacionFutura > 0
      ? `Aviso: Se descartaron ${response.operacionesConFechaOperacionFutura} operaciones con Fecha Operacion posterior a la fecha de referencia. No fueron utilizadas para calcular actividad.`
      : null,
    slugSummaryLine("Terminales activas", response?.terminalesActivas),
    slugSummaryLine("Con actividad reciente", response?.terminalesConActividadReciente),
    slugSummaryLine("Con historial pero sin actividad", response?.terminalesConHistorialSinActividad ?? response?.terminalesSinActividad),
    slugSummaryLine("Nunca operaron", response?.terminalesNuncaOperaron),
    slugSummaryLine("En periodo de gracia", response?.terminalesEnGraciaOnboarding),
    slugSummaryLine("Relacion ambigua", response?.terminalesIdentidadOperativaNoResuelta ?? response?.terminalesRelacionAmbigua),
    slugSummaryLine("Con proceso existente", response?.terminalesConProcesoExistente),
    response?.fechaCorte ? `Fecha de corte: ${response.fechaCorte}` : null,
    response?.motivoBloqueoEjecucionReal ? `Bloqueo: ${response.motivoBloqueoEjecucionReal}` : null,
  ]
    .filter(Boolean)
    .join("\n");

function slugSummaryLine(label, value) {
  return Number.isFinite(value) ? `${label}: ${value}` : null;
}

const buildDryRunCandidatesTable = (response, slug) => {
  if (!Array.isArray(response?.instancias) || response.instancias.length === 0) {
    return "";
  }

  if (slug === "terminal-sin-facturacion") {
    const rows = response.instancias
      .map((item) => {
        const motivo = item?.motivoOmitido || item?.motivoDeteccion || "-";
        return `
          <tr>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.cuit || "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.numeroComercio || "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.numeroTerminal || "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.razonSocial || item?.comercio || "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.ultimaFechaFacturacion || item?.ultimaVenta || "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.diasSinVentas ?? "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.estadoFacturacion || "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.totalHoy ?? "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.promedio7Dias ?? "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.patronHistorico || "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.umbralCalculado ?? "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.clasificacion || "-")}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(motivo)}</td>
            <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.entidadKey || "-")}</td>
          </tr>`;
      })
      .join("");

    return `
      <div style="margin-top:16px;text-align:left;">
        <div style="font-weight:600;margin-bottom:8px;">Listado de terminales evaluadas</div>
        <div style="max-height:320px;overflow:auto;border:1px solid #dee2e6;border-radius:8px;">
          <table style="width:100%;border-collapse:collapse;font-size:12px;">
            <thead style="position:sticky;top:0;background:#f8f9fa;">
              <tr>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">CUIT</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Comercio</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Terminal</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Razon social</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Ultima operacion</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Dias sin fact.</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Estado</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Total hoy</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Prom. 7 dias</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Patron</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Umbral</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Clasificacion</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Motivo</th>
                <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">EntidadKey</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      </div>`;
  }

  if (slug !== "zoco-check") {
    return "";
  }

  const rows = response.instancias
    .map((item) => {
      const motivo = item?.motivoOmitido || item?.motivoDeteccion || "-";
      return `
        <tr>
          <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.cuit || "-")}</td>
          <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.numeroComercio || "-")}</td>
          <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.razonSocial || item?.comercio || "-")}</td>
          <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.cantidadTerminalesAgrupadas ?? "-")}</td>
          <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.terminalesConActividad ?? "-")}</td>
          <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.terminalesSinActividadDetalle ?? "-")}</td>
          <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.ultimaFechaFacturacion || item?.ultimaVenta || "-")}</td>
          <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(motivo)}</td>
          <td style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">${escapeHtml(item?.entidadKeySemanal || item?.entidadKey || "-")}</td>
        </tr>`;
    })
    .join("");

  return `
    <div style="margin-top:16px;text-align:left;">
      <div style="font-weight:600;margin-bottom:8px;">Listado de candidatos evaluados</div>
      <div style="max-height:320px;overflow:auto;border:1px solid #dee2e6;border-radius:8px;">
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead style="position:sticky;top:0;background:#f8f9fa;">
            <tr>
              <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">CUIT</th>
              <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Comercio</th>
              <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Razon social</th>
              <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Terminales</th>
              <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Con actividad</th>
              <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Sin actividad</th>
              <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Ultima facturacion</th>
              <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">Motivo</th>
              <th style="padding:8px;border-bottom:1px solid #dee2e6;text-align:left;">EntidadKey semanal</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    </div>`;
};

export default function CRMPostventaV2Procesos({ slug = "zoco-check", embedded = false }) {
  const section = getSectionMeta(slug);
  const [fecha, setFecha] = useState(makeToday());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [existingEvidences, setExistingEvidences] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const loadRows = async () => {
    setLoading(true);
    try {
      if (slug === "historial") {
        const responses = await Promise.all(
          PROCESS_SLUGS.map((processSlug) => crmPostventaV2Api.obtenerCola(processSlug, { fecha }))
        );
        const merged = responses.flatMap((response, index) =>
          (response?.filas || []).map((item) => ({
            ...item,
            procesoSlug: PROCESS_SLUGS[index],
            procesoLabel: getSectionMeta(PROCESS_SLUGS[index]).shortTitle,
          }))
        );
        setRows(merged.sort((a, b) => String(a.fechaProximaAccion || "").localeCompare(String(b.fechaProximaAccion || ""))));
      } else {
        const response = await crmPostventaV2Api.obtenerCola(slug, { fecha });
        setRows((response?.filas || []).map((item) => ({ ...item, procesoSlug: slug, procesoLabel: section.shortTitle })));
      }
    } catch (error) {
      Swal.fire(section.shortTitle, error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, [slug, fecha]);

  const openDetail = async (item) => {
    try {
      setSubmitting(true);
      const processSlug = item.procesoSlug || slug;
      const [detailResponse, evidencesResponse] = await Promise.all([
        crmPostventaV2Api.obtenerDetalle(processSlug, item.id),
        crmPostventaV2Api.obtenerEvidencias(item.id),
      ]);
      setDetail({
        ...detailResponse,
        instancia: {
          ...detailResponse?.instancia,
          procesoCodigo: detailResponse?.instancia?.procesoCodigo || processSlug,
          procesoSlug: processSlug,
        },
      });
      setExistingEvidences(evidencesResponse || []);
      setModalOpen(true);
    } catch (error) {
      Swal.fire(section.shortTitle, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerator = async (dryRun) => {
    try {
      setSubmitting(true);
      const action = section.supportsReconcile ? crmPostventaV2Api.reconciliar : crmPostventaV2Api.generar;

      if (dryRun) {
        const response = await action(slug, { dryRun: true, fecha });
        const summaryText = buildGenerationSummary(response, slug);
        await Swal.fire({
          title: "Dry run completado",
          text: summaryText,
          html: `<pre style="text-align:left;white-space:pre-wrap;margin:0;">${escapeHtml(summaryText)}</pre>${buildDryRunCandidatesTable(response, slug)}`,
          icon: "success",
          width: 1200,
        });
      } else {
        const preview = await action(slug, { dryRun: true, fecha });
        const confirmationText = [
          buildGenerationSummary(preview, slug),
          "",
          `Se crearan ${preview?.crear ?? 0} procesos reales, limitados a ${preview?.maximumItemsPerRun ?? 0} por ejecucion.`,
        ].join("\n");

        if (!preview?.permiteEjecucionReal) {
          await Swal.fire({
            title: "Ejecucion bloqueada",
            text: confirmationText,
            html: `<pre style="text-align:left;white-space:pre-wrap;margin:0;">${escapeHtml(confirmationText)}</pre>${buildDryRunCandidatesTable(preview, slug)}`,
            icon: "warning",
            width: 1200,
          });
          return;
        }

        const confirmation = await Swal.fire({
          title: "Confirmar ejecucion real",
          text: confirmationText,
          html: `<pre style="text-align:left;white-space:pre-wrap;margin:0;">${escapeHtml(confirmationText)}</pre>${buildDryRunCandidatesTable(preview, slug)}`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: section.actionLabel || "Ejecutar",
          cancelButtonText: "Cancelar",
          width: 1200,
        });

        if (!confirmation.isConfirmed) {
          return;
        }

        const response = await action(slug, { dryRun: false, fecha });
        await Swal.fire("Operacion completada", buildGenerationSummary(response, slug), "success");
      }

      await loadRows();
    } catch (error) {
      Swal.fire(section.shortTitle, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitAction = async (payload) => {
    if (!detail?.instancia?.id) return;

    try {
      setSubmitting(true);
      const response = await crmPostventaV2Api.ejecutarAccion(detail.instancia.procesoSlug || slug, detail.instancia.id, payload);
      await Swal.fire(section.shortTitle, response?.message || "Accion registrada.", "success");
      setModalOpen(false);
      setDetail(null);
      setExistingEvidences([]);
      await loadRows();
    } catch (error) {
      Swal.fire(section.shortTitle, error.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const summary = useMemo(
    () => ({
      total: rows.length,
      derivados: rows.filter((item) => item.derivado).length,
      checklistPendiente: rows.filter((item) => item.checklist?.length && !item.checklistCompletado).length,
    }),
    [rows]
  );

  const content = (
    <>
      <section className={`crm-postventa-v2-panel p-4${embedded ? "" : " mt-4"}`}>
        <div className="d-flex flex-wrap gap-3 justify-content-between align-items-end">
          <div>
            <div className="crm-postventa-kicker">{embedded ? "Seguimiento 2" : "CRM Postventa V2"}</div>
            <h2 className="crm-postventa-v2-section-heading mb-1">{section.shortTitle}</h2>
            <p className="crm-postventa-v2-section-subheading text-muted mb-0">{section.description}</p>
          </div>

          <div className="d-flex flex-wrap gap-2 align-items-end">
            <label className="d-flex flex-column gap-1">
              <span className="small fw-semibold">Fecha referencia</span>
              <input className="form-control" type="date" value={fecha} onChange={(event) => setFecha(event.target.value)} />
            </label>
            <Button variant="outline-secondary" onClick={loadRows} disabled={loading || submitting}>
              Actualizar
            </Button>
            {!section.isSpecial && (
              <>
                <Button variant="outline-primary" onClick={() => handleGenerator(true)} disabled={loading || submitting}>
                  Dry run
                </Button>
                {(section.supportsGenerate || section.supportsReconcile) && (
                  <Button variant="success" onClick={() => handleGenerator(false)} disabled={loading || submitting}>
                    {section.actionLabel || "Ejecutar"}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <section className="crm-postventa-v2-panel p-4 mt-4">
        <Alert variant="info" className="mb-3">
          Total visibles: {summary.total} | Derivadas: {summary.derivados} | Checklist pendiente: {summary.checklistPendiente}
        </Alert>

        {slug === "historial" && (
          <Alert variant="secondary">
            Esta vista consolida las colas activas de los procesos V2 para auditoria operativa rapida.
          </Alert>
        )}

        {loading ? (
          <div className="py-5 text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <ProcessQueueTable
            rows={rows}
            onManage={openDetail}
            loading={submitting}
            includeProcess={slug === "historial" || slug === "derivaciones"}
          />
        )}
      </section>
    </>
  );

  return (
    <>
      {embedded ? content : (
        <section className="crm-postventa-page" translate="no">
          <div className="container py-4">{content}</div>
        </section>
      )}

      <ProcessActionModal
        show={modalOpen}
        onHide={() => setModalOpen(false)}
        detail={detail}
        existingEvidences={existingEvidences}
        submitting={submitting}
        onSubmit={handleSubmitAction}
      />
    </>
  );
}
